import { useState, useCallback } from 'react'
import { apiPost } from '../../../../../generic/Api_Functions'
import useMosInfo from '../../../common/provider/useMosInfo'
import { PERIOD_KEYS, injectPeriodFilter } from '../utils/periodFilter'
import { NO_COMPONENT, componentPathSegment } from '../utils/componentFilter'

/**
 * Hook to manage planned jobs data fetching.
 * @param {string} [componentCode] - Story 219: the FULL code of the chosen component, or NO_COMPONENT.
 * Every list that accepts a component -- the device queue included -- carries it as the last path
 * segment; "All Active Jobs" and "External jobs" ignore it.
 * @param {string} [periodKey] - One of PERIOD_KEYS (defaults to ALL/no filter). Injected as a
 * `between` column filter right before every apiPost call, composing with whatever column filters
 * the table's own generic filtering already sends. The axis is `startDate` for most fetchers, but
 * TWO of them narrow by a date of their own -- `fetchExternalCircuitJobs` by `actualDispatch` and
 * `fetchFinishedInDeviceJobs` by `endDate` -- because those are the only date their route projects
 * and accepts. Sending an axis a route does not support is NOT an error: Sun drops the key in
 * silence and answers UNFILTERED.
 * @returns {Object} Hook functions and state
 */
const usePlannedJobs = (componentCode = NO_COMPONENT, periodKey = PERIOD_KEYS.ALL) => {
  const { device } = useMosInfo()
  const [error, setError] = useState(null)

  const componentSegment = componentPathSegment(componentCode)
  const withPeriod = useCallback((pagination) => injectPeriodFilter(pagination, periodKey), [periodKey])

  const fetchPlannedJobs = useCallback(async (pagination) => {
    try {
      if (!device?.id) return []
      // Story 219 (contract §1): without a component, today's exact route, trailing slash included.
      const queuePath = 'mos/planned/device/' + device.id + (componentSegment === '' ? '/' : componentSegment)
      return await apiPost(queuePath, withPeriod(pagination))
    } catch (err) {
      console.error('Error fetching planned jobs:', err)
      setError(err)
      return []
    }
  }, [device?.id, componentSegment, withPeriod])

  const fetchAllPlannedJobs = useCallback(async (pagination) => {
    try {
      if (!device?.id) return []
      return await apiPost('mos/allPlanned/device/' + device.id + componentSegment, withPeriod(pagination))
    } catch (err) {
      console.error('Error fetching all planned jobs:', err)
      setError(err)
      return []
    }
  }, [device?.id, componentSegment, withPeriod])

  // Story 193 (MIC-4346, S31/S33/S34): the "All Jobs" tab -- the planned jobs of the chosen
  // technologies, on ANY asset. Same path shape (component segment included) and same body (period
  // and column filters) as `fetchAllPlannedJobs`; the technologies travel in the query because the
  // body is already the shared QueryBuilder. They arrive as an argument, not through a closure, so
  // the identity of this fetcher never depends on the selection. Without technologies it never asks
  // (P3: an empty selection is not "every technology") -- Sun would answer empty anyway.
  const fetchPlannedByTechnologyJobs = useCallback(async (pagination, technologyIds = []) => {
    try {
      if (!device?.id || technologyIds.length === 0) return { content: [], totalElements: 0 }
      const query = '?technologyIds=' + technologyIds.join(',')
      return await apiPost('mos/allPlannedByTechnology/device/' + device.id + componentSegment + query, withPeriod(pagination))
    } catch (err) {
      console.error('Error fetching planned jobs by technology:', err)
      setError(err)
      return []
    }
  }, [device?.id, componentSegment, withPeriod])

  const fetchCompatibleJobs = useCallback(async (pagination) => {
    try {
      if (!device?.id) return []
      return await apiPost('mos/allPlannedCompatible/device/' + device.id + componentSegment, withPeriod(pagination))
    } catch (err) {
      console.error('Error fetching compatible jobs:', err)
      setError(err)
      return []
    }
  }, [device?.id, componentSegment, withPeriod])

  const fetchFinishedCompatibleJobs = useCallback(async (pagination) => {
    try {
      if (!device?.id) return []
      return await apiPost('mos/allPlannedCompatibleFinished/device/' + device.id + componentSegment, withPeriod(pagination))
    } catch (err) {
      console.error('Error fetching finished compatible jobs:', err)
      setError(err)
      return []
    }
  }, [device?.id, componentSegment, withPeriod])

  const fetchAllActiveJobs = useCallback(async (pagination) => {
    try {
      if (!device?.id) return []
      return await apiPost('mos/allActive/device/' + device.id, withPeriod(pagination))
    } catch (err) {
      console.error('Error fetching all active jobs:', err)
      setError(err)
      return []
    }
  }, [device?.id, withPeriod])

  // Story 72 (S16/S18, RN12/RN13): rework jobs (jobReference.rework = true) of the whole
  // installation, technology-compatible with the querying device and not finished, paginated
  // server-side. Same request/response contract as the sibling fetchers
  // (Page<CompassJobReferenceDto>, rows carry `isRework`). Incompatible reworks are never
  // returned, so the tab needs no client-side filtering.
  const fetchReworkJobs = useCallback(async (pagination) => {
    try {
      if (!device?.id) return []
      return await apiPost('mos/allPlannedCompatibleRework/device/' + device.id + componentSegment, withPeriod(pagination))
    } catch (err) {
      console.error('Error fetching rework jobs:', err)
      setError(err)
      return []
    }
  }, [device?.id, componentSegment, withPeriod])

  // Story 100 (S106, RN33/RN46/DN-10/DN-13/C12): the "External jobs" tab -- installation-wide trips
  // of an external job of the circuit, SOLELY visibility. Same shape as `fetchAllActiveJobs` (no
  // `componentCode` filter): the tab is not restricted by the querying device's technology either.
  // The period filter narrows by `actualDispatch` instead of the default `startDate` -- a trip has
  // no "start date" of its own, `actualDispatch` is its closest equivalent (RN37/RN38.1, no domain
  // decision here, purely a column-name choice for the shared PeriodSelect).
  const fetchExternalCircuitJobs = useCallback(async (pagination) => {
    try {
      if (!device?.id) return []
      return await apiPost('mos/allExternalCircuit/device/' + device.id, injectPeriodFilter(pagination, periodKey, 'actualDispatch'))
    } catch (err) {
      console.error('Error fetching external circuit jobs:', err)
      setError(err)
      return []
    }
  }, [device?.id, periodKey])

  // Story 122 (S44/S48/S49/S50): the "Finished Jobs of This Asset" tab -- the jobs that were
  // FINISHED ON the querying device, read from the current row of their execution footprint, unlike
  // `fetchFinishedCompatibleJobs`, which is installation-wide and narrowed only by technology
  // compatibility. Same request/response contract as its siblings (Page<CompassJobReferenceDto>,
  // 19 fields, none new), so the tab reuses the shared columns and does no client-side filtering.
  //
  // Two things differ from every sibling, and BOTH fail silently if wrong (contract §7):
  //
  //  1. The route is `mos/allFinished/device/...`, not the `mos/allPlannedFinished/...` of the
  //     withdrawn delivery: that path, its source and its order no longer exist in Sun.
  //  2. The period filter narrows by `endDate` -- the instant the job was finished -- instead of
  //     the default `startDate`, which this read does not project at all (it always arrives null,
  //     contract §3.2). Sun's whitelist for this route DROPS an unsupported column key WITHOUT any
  //     error and answers UNFILTERED, so leaving `startDate` here would send a filter the server
  //     silently ignores and the operator would believe they had narrowed the list. The third
  //     argument is the same one the external-circuit tab already uses with 'actualDispatch'.
  const fetchFinishedInDeviceJobs = useCallback(async (pagination) => {
    try {
      if (!device?.id) return []
      const withFinishDatePeriod = injectPeriodFilter(pagination, periodKey, 'endDate')
      return await apiPost('mos/allFinished/device/' + device.id + componentSegment, withFinishDatePeriod)
    } catch (err) {
      console.error('Error fetching finished in-device jobs:', err)
      setError(err)
      return []
    }
  }, [device?.id, componentSegment, periodKey])

  return {
    fetchPlannedJobs,
    fetchAllPlannedJobs,
    fetchPlannedByTechnologyJobs,
    fetchCompatibleJobs,
    fetchFinishedCompatibleJobs,
    fetchAllActiveJobs,
    fetchReworkJobs,
    fetchExternalCircuitJobs,
    fetchFinishedInDeviceJobs,
    error,
    setError
  }
}

export default usePlannedJobs
